// Pure positioning math for the DatePicker calendar popup.
//
// Regression: GitHub issue #79 — "Calendar UI overlapping with cards on
// Demand Forecast page". The calendar used `position: absolute` inside a
// filter bar that has `backdrop-blur` (the GLASS token). backdrop-filter
// creates a new stacking context, which traps the popup's z-index *below*
// sibling cards that also create stacking contexts later in the DOM. The
// fix mirrors the existing Dropdown component: portal the popup to
// document.body and anchor it with `position: fixed` using the trigger's
// bounding rect. This helper computes those coordinates and keeps the
// popup inside the viewport.
//
// Regression: GitHub issue #80 — "Calendar UI not scrollable and dates
// hidden on Commission Settlements page". The #79 fix anchored the popup
// but never bounded its height: when neither the space above nor below
// the trigger could fit the full calendar, the bottom date rows rendered
// off-screen and the popup had no internal scroll, so those dates were
// unreachable. The helper now also returns a `maxHeight` that fits the
// popup within the available viewport space; the DatePicker applies it
// together with `overflow-y: auto` so every date stays reachable.

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
}

export interface BoxSize {
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * Compute the fixed-position coordinates for the calendar popup.
 *
 * - Horizontally aligns the popup's left edge with the trigger, then clamps
 *   it so it never spills past either viewport edge.
 * - Vertically prefers placing the popup just below the trigger; flips it
 *   above when there is not enough room below and more room above.
 * - Always returns a `maxHeight` that keeps the popup fully inside the
 *   viewport, so the DatePicker can make it scroll instead of hiding the
 *   bottom date rows off-screen (issue #80).
 */
export function computeCalendarPosition(
  trigger: AnchorRect,
  calendar: BoxSize,
  viewport: Viewport,
  gap = 4,
  margin = 8,
): { top: number; left: number; maxHeight: number } {
  // ── Horizontal ───────────────────────────────────────────────────────────
  let left = trigger.left;
  if (left + calendar.width > viewport.width - margin) {
    left = viewport.width - calendar.width - margin;
  }
  if (left < margin) left = margin;

  // ── Vertical ─────────────────────────────────────────────────────────────
  // Usable space above / below the trigger once the gap and the viewport
  // edge margin are reserved. The popup is never allowed to exceed the
  // viewport height itself.
  const spaceBelow = viewport.height - trigger.bottom - gap - margin;
  const spaceAbove = trigger.top - gap - margin;
  const viewportCap = Math.max(0, viewport.height - margin * 2);

  let top: number;
  let maxHeight: number;
  if (calendar.height <= spaceBelow) {
    // Fits in full below the trigger — the common case.
    top = trigger.bottom + gap;
    maxHeight = calendar.height;
  } else if (spaceAbove > spaceBelow) {
    // More room above: flip up and cap to whatever fits there.
    maxHeight = Math.min(calendar.height, spaceAbove);
    top = trigger.top - gap - maxHeight;
    if (top < margin) top = margin;
  } else {
    // Not enough room either way — stay below but cap to the space left
    // so the popup can scroll instead of overflowing off-screen.
    top = trigger.bottom + gap;
    maxHeight = Math.max(0, spaceBelow);
  }

  maxHeight = Math.min(maxHeight, viewportCap);

  return { top, left, maxHeight };
}
